// src/pages/AccountingPage.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import * as api from "../services/api";
import Card from "../components/Card";
import Button from "../components/Button";
import Input from "../components/Input";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDaysIcon,
  BanknotesIcon,
  PlusCircleIcon,
  ReceiptRefundIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ScaleIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  ChevronRightIcon,
  ChevronLeftIcon,
  ChevronDoubleRightIcon,
  ChevronDoubleLeftIcon,
} from "@heroicons/react/24/outline";
import Modal from "../components/Modal";
import { useAuth } from "../hooks/useAuth";
import { UserRole } from "../types";

/**
 * Utilities
 */
const getTodayDateString = () => new Date().toISOString().split("T")[0];
// compute academic year string from today's date (July boundary)
const computeCurrentAcademicYear = () => {
  const now = new Date();
  const month = now.getMonth(); // 0..11
  const year = now.getFullYear();
  const start = month >= 7 ? year : year - 1; // July boundary
  return `${start}-${start + 1}`;
};

// generate fixed list (same as original fixed list behavior)
const generateFixedAcademicYears = (start = 2025, count = 11) =>
  Array.from({ length: count }).map((_, i) => `${start + i}-${start + i + 1}`);

/** animation variants */
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { staggerChildren: 0.08, delayChildren: 0.12, type: "spring", stiffness: 120 },
  },
};
const itemVariants = {
  hidden: { y: 12, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

const LoadingSpinner = ({ label = "جاري التحميل..." }) => (
  <div role="status" aria-live="polite" className="flex justify-center items-center p-6">
    <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
    <span className="sr-only">{label}</span>
  </div>
);

/* ---------------- SmartPagination ----------------
 * Compact, ellipsis-aware page list. RTL friendly. Always shows first/last,
 * the active page, and a small window around it. Prev/Next and First/Last
 * arrows are visible icons. Pass current 1-indexed page, total pages, and
 * an onChange(page) callback.
 */
const buildPageWindow = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const window = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 3) [2, 3, 4].forEach((n) => window.add(n));
  if (current >= total - 2) [total - 1, total - 2, total - 3].forEach((n) => window.add(n));
  const sorted = [...window].filter((n) => n >= 1 && n <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("…");
  }
  return out;
};

const SmartPagination = ({ page, totalPages, onChange }) => {
  if (!totalPages || totalPages < 2) return null;
  const pages = buildPageWindow(page, totalPages);
  const go = (p) => p >= 1 && p <= totalPages && p !== page && onChange(p);

  const navBtn = (disabled, onClick, children, label) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  );

  return (
    <nav
      aria-label="صفحات"
      className="flex justify-center items-center p-4 gap-1.5 select-none"
    >
      {navBtn(page === 1, () => go(1), <ChevronDoubleRightIcon className="h-5 w-5" />, "الصفحة الأولى")}
      {navBtn(page === 1, () => go(page - 1), <ChevronRightIcon className="h-5 w-5" />, "السابق")}

      {pages.map((p, i) =>
        p === "…" ? (
          <span
            key={`gap-${i}`}
            className="inline-flex items-center justify-center h-10 w-10 text-slate-400 text-lg"
          >
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => go(p)}
            aria-current={page === p ? "page" : undefined}
            className={`inline-flex items-center justify-center h-10 min-w-10 px-3 rounded-lg text-sm font-semibold transition-all ${
              page === p
                ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-200"
                : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:text-slate-900"
            }`}
          >
            {p}
          </button>
        )
      )}

      {navBtn(page === totalPages, () => go(page + 1), <ChevronLeftIcon className="h-5 w-5" />, "التالي")}
      {navBtn(page === totalPages, () => go(totalPages), <ChevronDoubleLeftIcon className="h-5 w-5" />, "الصفحة الأخيرة")}

      <span className="hidden sm:inline-flex items-center mr-3 ms-3 text-sm text-slate-500">
        صفحة {page} من {totalPages}
      </span>
    </nav>
  );
};

/**
 * Derived constants memoized
 */
// old:
// const academicYears = generateAcademicYears(2025, 10);

// new:
const academicYears = generateFixedAcademicYears(2025, 11);
const computedAcademicYear = computeCurrentAcademicYear();
const initialComputedAcademicYear = academicYears.includes(computedAcademicYear) ? computedAcademicYear : academicYears[0];

/* ---------------- DailyCashTab ---------------- */
const DailyCashTab = ({ onViewInstallments, onViewExpenses }) => {
  const [date, setDate] = useState(getTodayDateString());
  const [dailyData, setDailyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchDailyCash = useCallback(
    async (d = date) => {
      setLoading(true);
      setDailyData(null);
      try {
        const { data } = await api.getCashForDay(d);
        if (!mountedRef.current) return;
        setDailyData({
          totalIn: Number(data.totalIn || 0),
          totalOut: Number(data.totalOut || 0),
          cash: Number(data.cash || (data.totalIn || 0) - (data.totalOut || 0)),
        });
      } catch (error) {
        console.error("fetchDailyCash:", error);
        toast.error(error?.response?.data?.error || "فشل في جلب بيانات الصندوق");
        setDailyData(null);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [date]
  );

  useEffect(() => {
    fetchDailyCash();
  }, [fetchDailyCash]);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      <motion.div variants={itemVariants} className="flex items-end gap-4 bg-white/60 p-4 rounded-xl shadow-sm border">
        <label htmlFor="daily-date" className="sr-only">
          اختر تاريخ
        </label>
        <Input id="daily-date" label="اختر تاريخ" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        <Button onClick={() => fetchDailyCash()} className="!py-2.5 !px-5 flex items-center gap-2" aria-label="عرض صندوق اليوم">
          <MagnifyingGlassIcon className="h-5 w-5" />
          <span>عرض</span>
        </Button>
      </motion.div>

      {loading && <LoadingSpinner />}

      {dailyData && (
        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            variants={itemVariants}
            onClick={() => onViewInstallments && onViewInstallments(date)}
            role="button"
            tabIndex={0}
            aria-pressed="false"
            className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex items-start gap-4 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          >
            <div className="bg-green-100 text-green-600 p-3 rounded-full">
              <ArrowTrendingUpIcon className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-slate-1000 font-bold font-medium">الإيرادات</h3>
              <p className="text-3xl font-bold text-green-600 mt-1">{dailyData.totalIn.toLocaleString()} ل.س</p>
              <p className="text-md text-slate-1000 mt-2">انقر لعرض التفاصيل ...</p>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            onClick={() => onViewExpenses && onViewExpenses(date)}
            role="button"
            tabIndex={0}
            className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex items-start gap-4 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
          >
            <div className="bg-red-100 text-red-600 p-3 rounded-full">
              <ArrowTrendingDownIcon className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-slate-1000 font-bold font-medium">المصروفات</h3>
              <p className="text-3xl font-bold text-red-600 mt-1">{dailyData.totalOut.toLocaleString()} ل.س</p>
              <p className="text-md text-slate-1000 mt-2">انقر لعرض التفاصيل ...</p>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex items-start gap-4">
            <div className="bg-blue-100 text-blue-600 p-3 rounded-full">
              <ScaleIcon className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-slate-1000 font-medium">الصافي</h3>
              <p className="text-3xl font-bold text-slate-800 mt-1">{dailyData.cash.toLocaleString()} ل.س</p>
              <p className="text-md text-slate-1000 mt-2">الإيرادات - المصروفات</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};


/* ---------------- AnnualBudgetTab ---------------- */
const AnnualBudgetTab = ({ onOpenInstallmentsForAcademicYear, onOpenExpensesForAcademicYear }) => {
const [academicYear, setAcademicYear] = useState(initialComputedAcademicYear);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => (mountedRef.current = false);
  }, []);

  const fetchAnnualSummary = useCallback(
    async (year) => {
      if (!year) {
        setSummaryData(null);
        return;
      }
      setLoading(true);
      setSummaryData(null);
      try {
        // Use server-side aggregation (totals) instead of summing paginated rows on the client.
        const [installmentsRes, expensesRes] = await Promise.all([
          api.getInstallments({ academicYear: year, page: 1, limit: 1 }),
          api.getExpenses({ academicYear: year, page: 1, limit: 1 }),
        ]);
        const totalIn = Number(installmentsRes?.data?.totals?.in || 0);
        const totals = expensesRes?.data?.totals || {};
        const totalOut =
          Number(totals.out || 0) +
          Number(totals.externalOut || 0) +
          Number(totals.teacherSalary || 0)+
          Number(totals.driverSalary || 0)+
          Number(totals.repairs || 0);

           // ← ADD THIS

        if (!mountedRef.current) return;
        setSummaryData({ totalIn, totalOut, net: totalIn - totalOut });
      } catch (error) {
        console.error("fetchAnnualSummary:", error);
        toast.error(error?.response?.data?.error || "فشل في جلب بيانات الميزانية السنوية");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchAnnualSummary(academicYear);
  }, [academicYear, fetchAnnualSummary]);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      <motion.div variants={itemVariants} className="flex items-end gap-4 bg-white/60 p-4 rounded-xl shadow-sm border">
        <div className="w-full max-w-sm">
          <label htmlFor="annualAcademicYear" className="block text-sm font-medium text-slate-900 mb-1">
            اختر العام الدراسي
          </label>
          <select
            id="annualAcademicYear"
            value={academicYear}
            onChange={(e) => setAcademicYear(e.target.value)}
            className="mt-1 block w-full border-gray-300 rounded-md p-2.5 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
          >
            {academicYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <Button onClick={() => fetchAnnualSummary(academicYear)} className="!py-2.5 !px-5 flex items-center gap-2" aria-label="عرض الملخص السنوي">
          <MagnifyingGlassIcon className="h-5 w-5" />
          <span>عرض</span>
        </Button>
      </motion.div>

      {loading && <LoadingSpinner />}

      {summaryData && !loading && (
        <motion.div variants={containerVariants} className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <motion.div
            variants={itemVariants}
            className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex items-start gap-4 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            onClick={() => onOpenInstallmentsForAcademicYear && onOpenInstallmentsForAcademicYear(academicYear)}
            role="button"
            tabIndex={0}
          >
            <div className="bg-green-100 text-green-600 p-3 rounded-full">
              <ArrowTrendingUpIcon className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-slate-1000  font-bold font-medium">إجمالي الإيرادات</h3>
              <p className="text-3xl font-bold text-green-600 mt-1">{summaryData.totalIn.toLocaleString()} ل.س</p>
              <p className="text-md text-slate-1000 mt-2">انقر لعرض أقساط هذا العام الدراسي ...</p>
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex items-start gap-4 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
            onClick={() => onOpenExpensesForAcademicYear && onOpenExpensesForAcademicYear(academicYear)}
            role="button"
            tabIndex={0}
          >
            <div className="bg-red-100 text-red-600 p-3 rounded-full">
              <ArrowTrendingDownIcon className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-slate-1000 font-bold font-medium">إجمالي المصروفات</h3>
              <p className="text-3xl font-bold text-red-600 mt-1">{summaryData.totalOut.toLocaleString()} ل.س</p>
              <p className="text-md text-slate-1000 mt-2">انقر لعرض نفقات هذا العام الدراسي ...</p>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-white p-6 rounded-2xl shadow-lg border border-slate-100 flex items-start gap-4">
            <div className="bg-blue-100 text-blue-600 p-3 rounded-full">
              <ScaleIcon className="h-7 w-7" />
            </div>
            <div>
              <h3 className="text-slate-1000 font-medium">الصافي</h3>
              <p className="text-3xl font-bold text-slate-800 mt-1">{summaryData.net.toLocaleString()} ل.س</p>
              <p className="text-md text-slate-1000 mt-2">الإيرادات - المصروفات</p>
            </div>
          </motion.div>
        </motion.div>
      )}

      {!summaryData && !loading && <motion.p variants={itemVariants} className="text-center text-slate-500 py-10">لا توجد بيانات لعرضها. الرجاء اختيار عام دراسي والضغط على عرض.</motion.p>}
    </motion.div>
  );
};


/* ---------------- AddPaymentTab ---------------- */
const AddPaymentTab = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [amount, setAmount] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [academicYear, setAcademicYear] = useState(academicYears[0]);
  const [date, setDate] = useState(getTodayDateString());
  const [notes, setNotes] = useState("");
  const debounceRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.searchStudents(query);
        setResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("searchStudents:", err);
        setResults([]);
      }
    }, 350);

    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const resetForm = () => {
    setQuery("");
    setResults([]);
    setSelectedStudent(null);
    setAmount("");
    setReceiptNumber("");
    setAcademicYear(academicYears[0]);
    setDate(getTodayDateString());
    setNotes("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent) {
      toast.error("يرجى اختيار الطالب من البحث");
      return;
    }
    const normalizedAmount = Number(amount);
    if (!normalizedAmount || normalizedAmount <= 0) {
      toast.error("الرجاء إدخال مبلغ صالح أكبر من صفر");
      return;
    }
    try {
      await api.addPaymentBySerial(selectedStudent.serialNumber, {
        academicYear,
        amount: normalizedAmount,
        receiptNumber,
        date,
        notes,
      });
      toast.success("تمت إضافة الدفعة بنجاح");
      resetForm();
    } catch (error) {
      console.error("addPayment:", error);
      toast.error(error?.response?.data?.error || "فشل في إضافة الدفعة");
    }
  };

  return (
    <motion.form onSubmit={handleSubmit} variants={containerVariants} initial="hidden" animate="visible" className="space-y-6 max-w-3xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
      <div className="relative">
        <Input label="ابحث عن الطالب (بالرقم التسلسلي أو الاسم)" id="studentSearch_payment" value={query} onChange={(e) => { setQuery(e.target.value); setSelectedStudent(null); }} />
        {results.length > 0 && !selectedStudent && (
          <ul className="absolute z-10 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto mt-1">
            {results.map((s) => (
              <li
                key={s.serialNumber}
                className="p-3 hover:bg-slate-50 cursor-pointer text-sm"
                onClick={() => {
                  const fullName = `${s.FirstName || ""} ${s.FatherName || ""} ${s.LastName || ""}`.trim();
                  setSelectedStudent({ serialNumber: s.serialNumber, fullName: fullName || s.serialNumber });
                  setQuery(`${fullName}`.trim());
                  setResults([]);
                }}
              >
                {s.serialNumber} - {s.FirstName} {s.FatherName} {s.LastName}
              </li>
            ))}
          </ul>
        )}
      </div>

      {selectedStudent && <div className="text-sm text-green-700 bg-green-50 p-3 rounded-lg font-semibold">✅ الطالب المحدد: {selectedStudent.fullName} ({selectedStudent.serialNumber})</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-lg text-slate-700">
        <Input label="المبلغ المستلم" id="amount_payment" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        <Input label="رقم الوصل" id="receiptNumber_payment" value={receiptNumber} onChange={(e) => setReceiptNumber(e.target.value)} />
        <div>
          <label htmlFor="academicYear_payment" className="block text-sm font-medium text-gray-700 mb-1">العام الدراسي</label>
          <select id="academicYear_payment" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
            {academicYears.map((year) => <option key={year} value={year}>{year}</option>)}
          </select>
        </div>
        <Input label="تاريخ الدفعة" id="date_payment" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
      </div>

      <Input label="ملاحظات" id="notes_payment" value={notes} onChange={(e) => setNotes(e.target.value)} />

      <Button type="submit" className="w-full !py-3 !text-base !font-bold">إضافة دفعة</Button>
    </motion.form>
  );
};

/* ---------------- AddExpenseTab (with Driver Salaries) ---------------- */
const AddExpenseTab = () => {
  const [activeSubTab, setActiveSubTab] = useState("general"); // 'general' | 'teacher' | 'driver'

  // ---------- General expense state (unchanged) ----------
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(getTodayDateString());
  const [isExternal, setIsExternal] = useState(false);
  const [academicYear, setAcademicYear] = useState(academicYears[0]);

  // ---------- Teacher salary state (unchanged) ----------
  const [teacherQuery, setTeacherQuery] = useState("");
  const [teacherResults, setTeacherResults] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null); // a teacher object
  const [hoursWorked, setHoursWorked] = useState("");
  const [teacherNotes, setTeacherNotes] = useState("");
  const [salaryDate, setSalaryDate] = useState(getTodayDateString()); // date used for salary tx (can be month indicator)
  const [teacherAcademicYear, setTeacherAcademicYear] = useState(academicYears[0]);
  const [loadingTeacherSearch, setLoadingTeacherSearch] = useState(false);

  // ---------- Driver (transport) salary state (unchanged) ----------
  const [driverQuery, setDriverQuery] = useState("");
  const [driverResults, setDriverResults] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null); // transport record
  const [driverNotes, setDriverNotes] = useState("");
  const [driverSalaryDate, setDriverSalaryDate] = useState(getTodayDateString());
  const [driverAcademicYear, setDriverAcademicYear] = useState(academicYears[0]);
  const [loadingDriverSearch, setLoadingDriverSearch] = useState(false);

  // ---------- Repairs state (NEW, only added) ----------
  const [repairAmount, setRepairAmount] = useState("");
  const [repairNotes, setRepairNotes] = useState("");
  const [repairDate, setRepairDate] = useState(getTodayDateString());
  const [repairAcademicYear, setRepairAcademicYear] = useState(academicYears[0]);
  const resetRepairForm = () => {
    setRepairAmount("");
    setRepairNotes("");
    setRepairDate(getTodayDateString());
    setRepairAcademicYear(academicYears[0]);
  };

  // debounces + mounted refs
  const teacherSearchDebounce = useRef(null);
  const driverSearchDebounce = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => (mountedRef.current = false);
  }, []);

  // ---------- Keep the old reset function for general ----------
  const resetGeneralForm = () => {
    setAmount("");
    setNotes("");
    setDate(getTodayDateString());
    setIsExternal(false);
    setAcademicYear(academicYears[0]);
  };

  // ---------- General expense submit (exact same logic) ----------
  const handleGeneralSubmit = async (e) => {
    e.preventDefault();
    const normalizedAmount = Number(amount);
    if (!normalizedAmount || normalizedAmount <= 0) {
      toast.error("الرجاء إدخال مبلغ صالح أكبر من صفر");
      return;
    }
    try {
      const expenseData = { amount: normalizedAmount, academicYear, notes, date };
      if (isExternal) {
        await api.addExternalExpense(expenseData);
        toast.success("تم تسجيل المصروف الخارجي بنجاح");
      } else {
        await api.addExpense(expenseData);
        toast.success("تم تسجيل المصروف بنجاح");
      }
      resetGeneralForm();
    } catch (error) {
      console.error("addExpense:", error);
      toast.error(error?.response?.data?.error || "فشل في تسجيل المصروف");
    }
  };

  // ---------- Teacher search (debounced) (unchanged) ----------
  useEffect(() => {
    if (!teacherQuery || teacherQuery.trim().length < 2) {
      setTeacherResults([]);
      return;
    }
    clearTimeout(teacherSearchDebounce.current);
    teacherSearchDebounce.current = setTimeout(async () => {
      try {
        setLoadingTeacherSearch(true);
        const { data } = await api.getTeachers({ search: teacherQuery.trim(), page: 1, limit: 10 });
        const items = data.items || data;
        if (!mountedRef.current) return;
        setTeacherResults(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error("teacherSearch:", err);
        setTeacherResults([]);
      } finally {
        if (mountedRef.current) setLoadingTeacherSearch(false);
      }
    }, 260);
    return () => clearTimeout(teacherSearchDebounce.current);
  }, [teacherQuery]);

  // ---------- Driver search (debounced) (unchanged) ----------
  useEffect(() => {
    if (!driverQuery || driverQuery.trim().length < 2) {
      setDriverResults([]);
      return;
    }
    clearTimeout(driverSearchDebounce.current);
    driverSearchDebounce.current = setTimeout(async () => {
      try {
        setLoadingDriverSearch(true);
        // Expect server to accept ?search=... and optional pagination for transport
        const { data } = await api.getTransports({ search: driverQuery.trim(), page: 1, limit: 10 });
        const items = data.items || data;
        if (!mountedRef.current) return;
        setDriverResults(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error("driverSearch:", err);
        setDriverResults([]);
      } finally {
        if (mountedRef.current) setLoadingDriverSearch(false);
      }
    }, 260);
    return () => clearTimeout(driverSearchDebounce.current);
  }, [driverQuery]);

  // ---------- When selecting teacher, clear hours/notes (unchanged) ----------
  useEffect(() => {
    setHoursWorked("");
    setTeacherNotes("");
  }, [selectedTeacher]);

  // ---------- When selecting driver, clear notes (unchanged) ----------
  useEffect(() => {
    setDriverNotes("");
  }, [selectedDriver]);

  // compute displayed salary for teacher (client-side convenience only)
  const computedTeacherSalary = (() => {
    if (!selectedTeacher) return 0;
    if (selectedTeacher.type === "fixed") return Number(selectedTeacher.monthlySalary || 0);
    const rate = Number(selectedTeacher.hourlyRate || 0);
    const hours = Number(hoursWorked || 0);
    return rate * hours;
  })();

  // driver computed salary (fixed monthly only)
  const computedDriverSalary = (() => {
    if (!selectedDriver) return 0;
    return Number(selectedDriver.monthlySalary || 0);
  })();

  // ---------- Submit salary for teacher (unchanged) ----------
  const handleTeacherSalarySubmit = async (e) => {
    e.preventDefault();
    if (!selectedTeacher) {
      toast.error("يرجى اختيار المدرس من البحث");
      return;
    }

    if (selectedTeacher.type === "hourly") {
      const hw = Number(hoursWorked);
      if (!hw || hw <= 0) {
        toast.error("الرجاء إدخال عدد ساعات صالح للمدرس العامل بالساعة");
        return;
      }
    }

    try {
      const dt = new Date(salaryDate);
      const month = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;

      const payload = {
        teacherId: selectedTeacher._id,
        month,
        hoursWorked: selectedTeacher.type === "hourly" ? Number(hoursWorked) : undefined,
        academicYear: teacherAcademicYear,
        date: salaryDate,
        notes: selectedTeacher.type === "hourly" ? (teacherNotes || "") : undefined,
      };

      await api.addTeacherSalary(payload);
      toast.success("تم تسجيل راتب المدرّس كمصروف بنجاح");

      // reset teacher form
      setSelectedTeacher(null);
      setTeacherQuery("");
      setTeacherResults([]);
      setHoursWorked("");
      setTeacherNotes("");
      setSalaryDate(getTodayDateString());
      setTeacherAcademicYear(academicYears[0]);
    } catch (err) {
      console.error("addTeacherSalary:", err);
      toast.error(err?.response?.data?.error || "فشل في تسجيل راتب المدرّس");
    }
  };

  // ---------- Submit salary for driver (unchanged) ----------
  const handleDriverSalarySubmit = async (e) => {
    e.preventDefault();
    if (!selectedDriver) {
      toast.error("يرجى اختيار السائق من البحث");
      return;
    }

    try {
      const dt = new Date(driverSalaryDate);
      const month = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;

      const payload = {
        driverId: selectedDriver._id,
        amount: selectedDriver.monthlySalary,
        month, // optional grouping by month
        academicYear: driverAcademicYear,
        date: driverSalaryDate,
        notes: driverNotes || undefined,
      };

      // NOTE: endpoint name - create a corresponding backend route to accept this
      await api.addDriverSalary(payload);
      toast.success("تم تسجيل راتب السائق كمصروف بنجاح");

      // reset driver form
      setSelectedDriver(null);
      setDriverQuery("");
      setDriverResults([]);
      setDriverNotes("");
      setDriverSalaryDate(getTodayDateString());
      setDriverAcademicYear(academicYears[0]);
    } catch (err) {
      console.error("addDriverSalary:", err);
      toast.error(err?.response?.data?.error || "فشل في تسجيل راتب السائق");
    }
  };

  // ---------- Submit repair expense (NEW) ----------
  const handleRepairSubmit = async (e) => {
    e.preventDefault();
    const normalizedAmount = Number(repairAmount);
    if (!normalizedAmount || normalizedAmount <= 0) {
      toast.error("الرجاء إدخال مبلغ مصروف صالح أكبر من صفر");
      return;
    }
    try {
      const payload = {
        amount: normalizedAmount,
        academicYear: repairAcademicYear,
        notes: repairNotes,
        date: repairDate,
      };
      // call backend endpoint for repairs (ensure api.addRepairExpense exists)
      await api.addRepairExpense(payload);
      toast.success("تم تسجيل مصروف الإصلاحات بنجاح");
      resetRepairForm();
    } catch (err) {
      console.error("addRepairExpense:", err);
      toast.error(err?.response?.data?.error || "فشل في تسجيل مصروف الإصلاحات");
    }
  };

  // ---------- small helpers (unchanged) ----------
  const selectTeacherFromResult = (t) => {
    setSelectedTeacher(t);
    setTeacherQuery(`${t.fullName}`);
    setTeacherResults([]);
  };

  const selectDriverFromResult = (d) => {
    setSelectedDriver(d);
    setDriverQuery(`${d.fullName}`);
    setDriverResults([]);
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      {/* Top subtab selector */}
      <div className="bg-white p-4 rounded-xl shadow-sm border flex gap-2 items-center">
        <div className={`flex gap-2 rounded-md overflow-hidden bg-slate-100 p-1`}>
          <button
            onClick={() => setActiveSubTab("general")}
            className={`px-4 py-2 text-sm font-semibold ${activeSubTab === "general" ? "bg-white shadow-md" : "text-slate-700"}`}
          >
            مصاريف عامة
          </button>

          <button
            onClick={() => setActiveSubTab("teacher")}
            className={`px-4 py-2 text-sm font-semibold ${activeSubTab === "teacher" ? "bg-white shadow-md" : "text-slate-700"}`}
          >
            رواتب مدرّسين
          </button>

          <button
            onClick={() => setActiveSubTab("driver")}
            className={`px-4 py-2 text-sm font-semibold ${activeSubTab === "driver" ? "bg-white shadow-md" : "text-slate-700"}`}
          >
            رواتب سائقين
          </button>

          {/* NEW: Repairs tab button (only addition) */}
          <button
            onClick={() => setActiveSubTab("repairs")}
            className={`px-4 py-2 text-sm font-semibold ${activeSubTab === "repairs" ? "bg-white shadow-md" : "text-slate-700"}`}
          >
            إصلاحات
          </button>
        </div>

        <div className="flex-1 text-md text-slate-900">اختر التبويب لإضافة مصروف عام أو صرف رواتب مدرسين أو سائقين أو إصلاحات</div>
      </div>

      {/* ------------------ GENERAL ------------------ */}
      {activeSubTab === "general" && (
        <motion.form onSubmit={handleGeneralSubmit} variants={itemVariants} className="space-y-4 max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
          <Input label="المبلغ المصروف" id="expenseAmount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          <div>
            <label htmlFor="academicYear_expense" className="block text-sm font-medium text-gray-700 mb-1">العام الدراسي</label>
            <select id="academicYear_expense" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm">
              {academicYears.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <Input label="ملاحظات" id="expenseNotes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <Input label="تاريخ المصروف" id="expenseDate" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          <div className="flex items-center">
            <input type="checkbox" id="isExternal" checked={isExternal} onChange={(e) => setIsExternal(e.target.checked)} className="h-4 w-4 rounded border-gray-300 ml-3" />
            <label htmlFor="isExternal" className="font-medium text-gray-700">مصروف خارجي (يخصم من الميزانية السنوية)</label>
          </div>
          <Button type="submit" className="w-full !py-3 !text-base !font-bold">تسجيل مصروف</Button>
        </motion.form>
      )}

      {/* ------------------ TEACHER SALARY ------------------ */}
      {activeSubTab === "teacher" && (
        <motion.form onSubmit={handleTeacherSalarySubmit} variants={itemVariants} className="space-y-4 max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
          {/* Search / autocomplete */}
          <div className="relative">
            <Input label="ابحث عن المدرّس" id="teacherSearch" value={teacherQuery} onChange={(e) => { setTeacherQuery(e.target.value); setSelectedTeacher(null); }} placeholder="اكتب اسم المدرس (بحد أدنى حرفين)" />
            {teacherResults.length > 0 && !selectedTeacher && (
              <ul className="absolute z-10 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-auto mt-1">
                {teacherResults.map(t => (
                  <li key={t._id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={() => selectTeacherFromResult(t)}>
                    <div className="font-medium">{t.fullName}</div>
                    <div className="text-xs text-slate-500">{t.type === 'fixed' ? `ثابت — ${t.monthlySalary ?? '-'} ل.س/شهر` : `بالساعة — ${t.hourlyRate ?? '-'} ل.س/ساعة`}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Selected teacher summary */}
          {selectedTeacher && (
            <div className="bg-slate-50 p-4 rounded-lg border">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-slate-800">{selectedTeacher.fullName}</div>
                  <div className="text-sm text-slate-600 mt-1">{selectedTeacher.type === 'fixed' ? 'معاش شهري ثابت' : 'أجر بالساعة'}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-500">القيمة الحالية</div>
                  <div className="text-xl font-bold text-slate-800">{selectedTeacher.type === 'fixed' ? (Number(selectedTeacher.monthlySalary || 0).toLocaleString() + ' ل.س') : (Number(selectedTeacher.hourlyRate || 0).toLocaleString() + ' ل.س/ساعة')}</div>
                </div>
              </div>

              {/* Common fields */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">العام الدراسي</label>
                  <select value={teacherAcademicYear} onChange={(e) => setTeacherAcademicYear(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm">
                    {academicYears.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الصرف</label>
                  <input type="date" value={salaryDate} onChange={(e) => setSalaryDate(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm" />
                </div>
              </div>

              {/* conditional: if hourly -> hours & notes; if fixed -> read-only display of salary */}
              {selectedTeacher.type === 'hourly' ? (
                <div className="mt-4 grid grid-cols-1 gap-3">
                  <Input label="عدد ساعات العمل" id="teacherHours" type="number" value={hoursWorked} onChange={(e) => setHoursWorked(e.target.value)} required />
                  <Input label="ملاحظات" id="teacherNotes" value={teacherNotes} onChange={(e) => setTeacherNotes(e.target.value)} />
                  <div className="text-sm text-slate-600 mt-1">الأجر المحتسب: <span className="font-semibold">{computedTeacherSalary.toLocaleString()} ل.س</span></div>
                </div>
              ) : (
                <div className="mt-4">
                  <div className="text-sm text-slate-600">المرتب الشهري الذي سيتم صرفه</div>
                  <div className="text-xl font-bold mt-1">{Number(selectedTeacher.monthlySalary || 0).toLocaleString()} ل.س</div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" className="flex-1">{selectedTeacher && selectedTeacher.type === 'hourly' ? 'صرف راتب (حسب الساعات)' : 'صرف مرتب شهري'}</Button>
            <Button type="button" variant="ghost" className="flex-1" onClick={() => {
              // reset teacher form
              setSelectedTeacher(null);
              setTeacherQuery("");
              setTeacherResults([]);
              setHoursWorked("");
              setTeacherNotes("");
              setSalaryDate(getTodayDateString());
              setTeacherAcademicYear(academicYears[0]);
            }}>مسح</Button>
          </div>
        </motion.form>
      )}

      {/* ------------------ DRIVER SALARY (unchanged) ------------------ */}
      {activeSubTab === "driver" && (
        <motion.form onSubmit={handleDriverSalarySubmit} variants={itemVariants} className="space-y-4 max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
          {/* Search / autocomplete for drivers (transport records) */}
          <div className="relative">
            <Input label="ابحث عن السائق" id="driverSearch" value={driverQuery} onChange={(e) => { setDriverQuery(e.target.value); setSelectedDriver(null); }} placeholder="اكتب اسم السائق (بحد أدنى حرفين)" />
            {driverResults.length > 0 && !selectedDriver && (
              <ul className="absolute z-10 w-full bg-white border rounded-md shadow-lg max-h-48 overflow-auto mt-1">
                {driverResults.map(d => (
                  <li key={d._id} className="p-3 hover:bg-slate-50 cursor-pointer" onClick={() => selectDriverFromResult(d)}>
                    <div className="font-medium">{d.fullName}</div>
                    <div className="text-xs text-slate-500">راتب شهري: {d.monthlySalary ?? "-" } ل.س</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Selected driver summary */}
          {selectedDriver && (
            <div className="bg-slate-50 p-4 rounded-lg border">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="font-semibold text-slate-800">{selectedDriver.fullName}</div>
                  <div className="text-sm text-slate-600 mt-1">سائق — سجل النقل</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-slate-500">المرتب الحالي</div>
                  <div className="text-xl font-bold text-slate-800">{Number(selectedDriver.monthlySalary || 0).toLocaleString()} ل.س</div>
                </div>
              </div>

              {/* Common fields */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">العام الدراسي</label>
                  <select value={driverAcademicYear} onChange={(e) => setDriverAcademicYear(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm">
                    {academicYears.map((year) => <option key={year} value={year}>{year}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الصرف</label>
                  <input type="date" value={driverSalaryDate} onChange={(e) => setDriverSalaryDate(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm" />
                </div>
              </div>

              {/* Notes + computed salary display */}
              <div className="mt-4 grid grid-cols-1 gap-3">
                <Input label="ملاحظات" id="driverNotes" value={driverNotes} onChange={(e) => setDriverNotes(e.target.value)} />
                <div className="text-sm text-slate-600 mt-1">المبلغ الذي سيتم صرفه: <span className="font-semibold">{computedDriverSalary.toLocaleString()} ل.س</span></div>
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="submit" className="flex-1">صرف مرتب السائق</Button>
            <Button type="button" variant="ghost" className="flex-1" onClick={() => {
              // reset driver form
              setSelectedDriver(null);
              setDriverQuery("");
              setDriverResults([]);
              setDriverNotes("");
              setDriverSalaryDate(getTodayDateString());
              setDriverAcademicYear(academicYears[0]);
            }}>مسح</Button>
          </div>
        </motion.form>
      )}

      {/* ------------------ REPAIRS (NEW) - only this block is added ------------------ */}
      {activeSubTab === "repairs" && (
        <motion.form onSubmit={handleRepairSubmit} variants={itemVariants} className="space-y-4 max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-slate-100">
          <Input label="المبلغ المصروف" id="repairAmount" type="number" value={repairAmount} onChange={(e) => setRepairAmount(e.target.value)} required />
          <div>
            <label htmlFor="academicYear_repair" className="block text-sm font-medium text-gray-700 mb-1">العام الدراسي</label>
            <select id="academicYear_repair" value={repairAcademicYear} onChange={(e) => setRepairAcademicYear(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm">
              {academicYears.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <Input label="ملاحظات" id="repairNotes" value={repairNotes} onChange={(e) => setRepairNotes(e.target.value)} />
          <Input label="تاريخ المصروف" id="repairDate" type="date" value={repairDate} onChange={(e) => setRepairDate(e.target.value)} required />
          <div className="flex gap-2">
            <Button type="submit" className="flex-1">تسجيل إصلاح</Button>
            <Button type="button" variant="ghost" className="flex-1" onClick={resetRepairForm}>مسح</Button>
          </div>
        </motion.form>
      )}
    </motion.div>
  );
};


/* ---------------- EditTransactionModal ----------------
 * Owner-only modal for fixing a previously-recorded installment or
 * expense. The field list shown adapts to the transaction's type:
 *   - "in"               → amount, receiptNumber, date, academicYear, notes
 *   - "teacher-salary"   → amount, month, hoursWorked, date, academicYear, notes
 *   - "driver-salary"    → amount, month, date, academicYear, notes
 *   - others             → amount, date, academicYear, notes
 */
const EditTransactionModal = ({ isOpen, onClose, transaction, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!transaction) return;
    setForm({
      amount:
        transaction.amount === null || transaction.amount === undefined || transaction.amount === ""
          ? ""
          : Math.round(Number(transaction.amount)),
      receiptNumber: transaction.receiptNumber ?? "",
      date: transaction.date ? new Date(transaction.date).toISOString().slice(0, 10) : "",
      academicYear: transaction.academicYear ?? "",
      notes: transaction.notes ?? "",
      month: transaction.month ?? "",
      hoursWorked: transaction.hoursWorked ?? "",
    });
  }, [transaction]);

  if (!transaction) return null;

  const t = transaction.type;
  const isInstallment = t === "in";
  const isTeacherSalary = t === "teacher-salary";
  const isDriverSalary = t === "driver-salary";

  const setField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        amount: Math.round(Number(form.amount)),
        date: form.date,
        academicYear: form.academicYear || null,
        notes: form.notes || null,
      };
      if (isInstallment) payload.receiptNumber = form.receiptNumber || null;
      if (isTeacherSalary || isDriverSalary) payload.month = form.month || null;
      if (isTeacherSalary)
        payload.hoursWorked =
          form.hoursWorked === "" || form.hoursWorked === null
            ? null
            : Number(form.hoursWorked);

      const { data } = await api.updateTransaction(transaction._id, payload);
      toast.success(data?.message || "تم تحديث القيد بنجاح");
      onSaved?.(data?.transaction);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.error || "فشل في التحديث");
    } finally {
      setSaving(false);
    }
  };

  const title = isInstallment ? "تعديل قسط" : "تعديل نفقة";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="المبلغ"
            id="edit_amount"
            type="number"
            min="0"
            step="1"
            value={form.amount}
            onChange={(e) =>
              setForm((p) => ({
                ...p,
                amount: e.target.value === "" ? "" : e.target.value.replace(/[^\d]/g, ""),
              }))
            }
            required
          />
          <Input
            label="التاريخ"
            id="edit_date"
            type="date"
            value={form.date}
            onChange={setField("date")}
            required
          />

          {isInstallment && (
            <Input
              label="رقم الوصل"
              id="edit_receipt"
              value={form.receiptNumber}
              onChange={setField("receiptNumber")}
            />
          )}

          <div>
            <label className="block text-sm font-medium text-slate-900 mb-1">
              العام الدراسي
            </label>
            <select
              value={form.academicYear || ""}
              onChange={setField("academicYear")}
              className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm"
            >
              <option value="">— بدون —</option>
              {academicYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {(isTeacherSalary || isDriverSalary) && (
            <Input
              label="الشهر"
              id="edit_month"
              value={form.month}
              onChange={setField("month")}
            />
          )}
          {isTeacherSalary && (
            <Input
              label="عدد الساعات"
              id="edit_hours"
              type="number"
              min="0"
              value={form.hoursWorked ?? ""}
              onChange={setField("hoursWorked")}
            />
          )}
        </div>

        <Input
          label="ملاحظات"
          id="edit_notes"
          value={form.notes}
          onChange={setField("notes")}
        />

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            إلغاء
          </Button>
          <Button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? "جاري الحفظ..." : "حفظ التعديل"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------------- InstallmentsTab ---------------- */
const InstallmentsTab = ({ initialDate, initialAcademicYear }) => {
const [academicYear, setAcademicYear] = useState(
  initialAcademicYear !== undefined ? initialAcademicYear : initialComputedAcademicYear
);
  const [date, setDate] = useState(initialDate || "");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // student search states
  const [studentQuery, setStudentQuery] = useState("");
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Edit modal (owner-only)
  const { user } = useAuth();
  const isOwner = user?.role === UserRole.OWNER;
  const [editTarget, setEditTarget] = useState(null);

  const mountedRef = useRef(true);
  const studentDebounceRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => (mountedRef.current = false);
  }, []);

  const fetchTransactions = useCallback(
    async (p = 1) => {
      setLoading(true);
      try {
        const params = { page: p };
        if (academicYear) params.academicYear = academicYear;
        if (date) params.date = date;
        if (receiptNumber) params.receiptNumber = receiptNumber;
        if (selectedStudent?.serialNumber) params.studentSerialNumber = selectedStudent.serialNumber;

        const { data } = await api.getInstallments(params);
        if (!mountedRef.current) return;
        setTransactions(data.transactions || []);
        setTotalPages(data.totalPages || Math.max(1, Math.ceil((data.total || data.transactions?.length || 0) / (data.pageSize || 10))));
        setPage(data.page || p);
      } catch (err) {
        console.error("fetchTransactions:", err);
        toast.error("فشل في جلب الأقساط");
        if (mountedRef.current) {
          setTransactions([]);
          setTotalPages(1);
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [academicYear, date, receiptNumber, selectedStudent]
  );

  useEffect(() => {
    // fetch page 1 whenever filters change
    setPage(1);
    fetchTransactions(1);
  }, [academicYear, date, receiptNumber, selectedStudent, fetchTransactions]);

  // respond to parent-provided initial values
  useEffect(() => {
    if (initialDate !== undefined) {
      setDate(initialDate);
      setPage(1);
    }
    if (initialAcademicYear !== undefined) {
      setAcademicYear(initialAcademicYear);
      setPage(1);
    }
    fetchTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate, initialAcademicYear]);

  // student search effect
  useEffect(() => {
    if (!studentQuery || studentQuery.trim().length < 2) {
      setStudentResults([]);
      return;
    }
    clearTimeout(studentDebounceRef.current);
    studentDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.searchStudents(studentQuery);
        setStudentResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("searchStudents in InstallmentsTab:", err);
        setStudentResults([]);
      }
    }, 350);
    return () => clearTimeout(studentDebounceRef.current);
  }, [studentQuery]);

  const clearSelectedStudent = () => {
    setSelectedStudent(null);
    setStudentQuery("");
    setStudentResults([]);
    setPage(1);
  };

  const totalAmount = transactions.reduce((sum, t) => sum + Number(t.amount || 0), 0);

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      <div className="bg-white/60 p-4 rounded-xl text-md font-medium shadow-sm border space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="academicYear_installments" className="block text-md font-medium text-slate-900 mb-1">العام الدراسي</label>
            <select id="academicYear_installments" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)} className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50">
              <option value="">الكل / بدون فلترة بالسنة</option>
              {academicYears.map((year) => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>

          <Input label="تاريخ" id="date_installments" type="date" value={date} onChange={(e) => setDate(e.target.value)}  />
          <Input label="رقم الوصل" id="receiptNumber_installments" value={receiptNumber} onChange={(e) => setReceiptNumber(e.target.value)} />

          <div className="relative">
            <label htmlFor="studentSearch_installments" className="block text-sm font-medium text-slate-900 mb-1">ابحث عن الطالب</label>
            <input
              id="studentSearch_installments"
              value={selectedStudent ? `${selectedStudent.fullName} (${selectedStudent.serialNumber})` : studentQuery}
              onChange={(e) => {
                if (selectedStudent) setSelectedStudent(null);
                setStudentQuery(e.target.value);
              }}
              placeholder="الاسم أو الرقم التسلسلي"
              className="mt-1 block w-full border-gray-300 rounded-md p-2 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              aria-autocomplete="list"
            />

            {studentResults.length > 0 && !selectedStudent && (
              <ul className="absolute z-20 left-0 right-0 bg-white border rounded-md shadow-lg max-h-60 overflow-auto mt-1">
                {studentResults.map((s) => {
                  const fullname = `${s.FirstName || ""} ${s.FatherName || ""} ${s.LastName || ""}`.trim();
                  return (
                    <li key={s.serialNumber} className="p-3 hover:bg-slate-50 cursor-pointer text-sm" onClick={() => {
                      setSelectedStudent({ serialNumber: s.serialNumber, fullName: fullname || s.serialNumber });
                      setStudentQuery("");
                      setStudentResults([]);
                      setPage(1);
                    }}>
                      <div className="text-lg font-medium">{s.FirstName} {s.FatherName} {s.LastName}</div>
                      <div className="text-md text-slate-700">{s.serialNumber}</div>
                    </li>
                  );
                })}
              </ul>
            )}

            {selectedStudent && (
              <div className="mt-2 flex items-center gap-3">
                <div className="bg-green-50 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">{selectedStudent.fullName} ({selectedStudent.serialNumber})</div>
                <button type="button" onClick={clearSelectedStudent} className="text-sm text-slate-500 underline">مسح</button>
              </div>
            )}
          </div>
        </div>

        <div className="pt-2">
          <Button onClick={() => { setPage(1); fetchTransactions(1); }} className="!px-6">بحث</Button>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && transactions.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-200 shadow-lg bg-white">
          <div className="bg-slate-100 p-3 text-center font-bold text-blue-800">مجموع الأقساط في هذه الصفحة: {totalAmount.toLocaleString()} ل.س</div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xl">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-right p-4 text-xl text-slate-900">الرقم التسلسلي</th>
                  <th className="text-right p-4 text-xl text-slate-900"> الاسم الكامل</th>
                  <th className="text-right p-4 text-xl text-slate-900">المبلغ</th>
                  <th className="text-right p-4 text-xl text-slate-900">رقم الوصل</th>
                  <th className="text-right p-4 text-xl text-slate-900">ملاحظات</th>
                  <th className="text-right p-4 text-xl text-slate-900">التاريخ</th>
                  <th className="text-right p-4 text-xl text-slate-900">العام الدراسي</th>
                  {isOwner && (
                    <th className="text-center p-4 text-xl text-slate-900">إجراءات</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transactions.map((t) => (
                  <tr key={t._id} className="hover:bg-slate-50 transition-colors">
                    <td className="p-4 whitespace-nowrap font-medium text-slate-900">{t.studentSerialNumber}</td>
                    <td className="p-4 whitespace-nowrap font-medium text-slate-900">{t.fullName}</td>
                    <td className="p-4 whitespace-nowrap text-green-600 font-semibold">{Number(t.amount || 0).toLocaleString()}</td>
                    <td className="p-4 whitespace-nowrap font-medium text-slate-900">{t.receiptNumber || "-"}</td>
                    <td className="p-4 text-slate-900 max-w-xs font-medium truncate">{t.notes || "-"}</td>
                    <td className="p-4 whitespace-nowrap font-medium text-slate-900">{t.date ? new Date(t.date).toLocaleDateString("ar-EG") : "-"}</td>
                    <td className="p-4 whitespace-nowrap font-medium text-slate-900 font-medium">{t.academicYear || "-"}</td>
                    {isOwner && (
                      <td className="p-4 text-center">
                        <button
                          onClick={() => setEditTarget(t)}
                          title="تعديل القسط"
                          className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <SmartPagination page={page} totalPages={totalPages} onChange={fetchTransactions} />
        </div>
      )}

      {!loading && transactions.length === 0 && <p className="text-center text-slate-500 py-10">لا توجد بيانات</p>}

      <EditTransactionModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        transaction={editTarget}
        onSaved={() => fetchTransactions(page)}
      />
    </motion.div>
  );
};
/* ---------------- ExpensesTab ---------------- */
const ExpensesTab = ({ initialDate, initialType, initialAcademicYear }) => {
  const [academicYear, setAcademicYear] = useState(
    initialAcademicYear !== undefined ? initialAcademicYear : initialComputedAcademicYear
  );
  const [date, setDate] = useState(initialDate || "");
  const [typeFilter, setTypeFilter] = useState(initialType || "");
  const [expenses, setExpenses] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // ✅ Add repairs into totals — without touching anything else
  const [totals, setTotals] = useState({
    out: 0,
    externalOut: 0,
    teacherSalary: 0,
    driverSalary: 0,
    repairs: 0,
  });

  // Edit modal (owner-only)
  const { user } = useAuth();
  const isOwner = user?.role === UserRole.OWNER;
  const [editTarget, setEditTarget] = useState(null);

  const mountedRef = useRef(true);

  // ---------------- FETCHING ----------------
  const fetchExpenses = useCallback(
    async (p = 1) => {
      setLoading(true);
      try {
        const params = { page: p };
        if (date) params.date = date;
        if (typeFilter) params.type = typeFilter;
        if (academicYear) params.academicYear = academicYear;

        const { data } = await api.getExpenses(params);
        if (!mountedRef.current) return;

        setExpenses(data.expenses || []);
        setTotalPages(
          data.totalPages ||
            Math.max(1, Math.ceil((data.total || data.expenses?.length || 0) / (data.pageSize || 10)))
        );
        setPage(data.page || p);

        // Keep everything but include repairs if provided by backend
        setTotals(
          data.totals || {
            out: 0,
            externalOut: 0,
            teacherSalary: 0,
            driverSalary: 0,
            repairs: 0,
          }
        );
      } catch (err) {
        console.error("fetchExpenses:", err);
        toast.error("فشل في جلب سجل النفقات");
        if (mountedRef.current) {
          setExpenses([]);
          setTotalPages(1);
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [academicYear, date, typeFilter]
  );

  useEffect(() => {
    fetchExpenses(1);
  }, [academicYear, date, typeFilter]);

  useEffect(() => {
    mountedRef.current = true;
    return () => (mountedRef.current = false);
  }, []);

  const totalAmount = expenses.reduce((sum, t) => sum + Number(t.amount || 0), 0);

  // ---------------- UI ----------------
  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">

      {/* FILTER BAR */}
      <div className="bg-white/60 p-4 rounded-xl shadow-sm border space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">

          <div>
            <label htmlFor="academicYear" className="block text-md font-medium text-slate-900 mb-1">
              العام الدراسي
            </label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 p-2 shadow-sm"
            >
              <option value="">الكل</option>
              {academicYears.map((y) => (
                <option key={y}>{y}</option>
              ))}
            </select>
          </div>

          <Input 
            label="تاريخ"
            id="dateFilter"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />

          <div>
            <label className="block text-md font-medium text-slate-900 mb-1">نوع النفقة</label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 p-2 shadow-sm"
            >
              <option value="">الكل</option>
              <option value="out">نفقات داخلية</option>
              <option value="external-out">نفقات خارجية</option>
              <option value="teacher-salary">رواتب مدرّسين</option>
              <option value="driver-salary">رواتب سائقين</option>

              {/* ⭐ NEW OPTION */}
              <option value="repairs">إصلاحات</option>
            </select>
          </div>

          <div className="flex items-end">
            <Button className="w-full" onClick={() => fetchExpenses(1)}>
              بحث
            </Button>
          </div>
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {!loading && expenses.length > 0 && (
        <div className="overflow-hidden rounded-xl border shadow-lg bg-white">

          <div className="bg-slate-100 p-3 text-center font-bold text-blue-800">
            مجموع النفقات في هذه الصفحة: {totalAmount.toLocaleString()} ل.س
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-xl">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-4">نوع النفقة</th>
                  <th className="p-4">المبلغ المصروف</th>
                  <th className="p-4 text-right">تفاصيل</th>
                  <th className="p-4">تاريخ النفقة</th>
                  <th className="p-4">العام الدراسي</th>
                  {isOwner && <th className="p-4 text-center">إجراءات</th>}
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {expenses.map((exp) => {
                  const isTeacher = exp.type === "teacher-salary";
                  const isDriver = exp.type === "driver-salary";
                  const isRepair = exp.type === "repairs"; // ⭐ NEW TYPE

                  return (
                    <tr key={exp._id} className="hover:bg-slate-50">

                      {/* TYPE BADGE */}
                      <td className="p-4 whitespace-nowrap ">

                        {exp.type === "teacher-salary" && (
                          <span className="w-full inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-100 text-purple-700 font-semibold text-sm">
                            <span className="h-2 w-2 rounded-full bg-purple-700"></span>
                            راتب مدرس
                          </span>
                        )}

                        {exp.type === "driver-salary" && (
                          <span className="w-full inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                            <span className="h-2 w-2 rounded-full bg-blue-700"></span>
                            راتب سائق
                          </span>
                        )}

                        {exp.type === "out" && (
                          <span className="w-full inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm">
                            <span className="h-2 w-2 rounded-full bg-orange-700"></span>
                            نفقة داخلية
                          </span>
                        )}

                        {exp.type === "external-out" && (
                          <span className="w-full inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-700 font-semibold text-sm">
                            <span className="h-2 w-2 rounded-full bg-red-700"></span>
                            نفقة خارجية
                          </span>
                        )}

                        {/* ⭐ NEW BADGE */}
                        {exp.type === "repairs" && (
                          <span className="w-full inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-100 text-green-700 font-semibold text-sm">
                            <span className="h-2 w-2 rounded-full bg-green-700"></span>
                            إصلاحات
                          </span>
                        )}
                      </td>

                      {/* AMOUNT */}
                      <td className="p-4 text-red-600 font-semibold text-center">
                        {Number(exp.amount).toLocaleString()}
                      </td>

                      {/* DETAILS */}
                      <td className="p-4 max-w-xs text-slate-900">

                        {isTeacher ? (
                          <div className="space-y-1">
                            <div className="font-bold"> الاسم : {exp.teacherName}</div>
                            <div className="text-sm text-slate-900"> النوع: {exp.salaryType === "fixed" ? "ثابت" : "ساعات"}</div>
                            <div className="text-sm text-slate-900">الشهر: {exp.month}</div>
                            {exp.notes && <div className="text-sm text-slate-900">ملاحظة: {exp.notes}</div>}
                          </div>
                        ) : isDriver ? (
                          <div className="space-y-1">
                            <div className="font-bold">الاسم : {exp.driverName || "—"}</div>
                            <div className="text-sm text-slate-900">الشهر: {exp.month}</div>
                            {exp.notes && <div className="text-sm text-slate-900">ملاحظة: {exp.notes}</div>}
                          </div>
                        ) : isRepair ? (
                          /* ⭐ NEW DETAILS BLOCK */
                          <div className="space-y-1">
                            <div className="font-bold">إصلاح</div>
                            {exp.notes && <div className="text-sm text-slate-900">ملاحظة: {exp.notes}</div>}
                          </div>
                        ) : (
                          <span className="p-4 font-medium text-xl text-left">
                            {exp.notes || "-"}
                          </span>
                        )}
                      </td>

                      {/* DATE */}
                      <td className="p-4 whitespace-nowrap text-slate-900 text-xl text-center">
                        {new Date(exp.date).toLocaleDateString("ar-EG")}
                      </td>

                      {/* YEAR */}
                      <td className="p-4 font-medium text-center">{exp.academicYear || "-"}</td>

                      {/* ACTIONS (owner only) */}
                      {isOwner && (
                        <td className="p-4 text-center">
                          <button
                            onClick={() => setEditTarget(exp)}
                            title="تعديل النفقة"
                            className="inline-flex items-center justify-center h-9 w-9 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* PAGINATION */}
          <SmartPagination page={page} totalPages={totalPages} onChange={fetchExpenses} />
        </div>
      )}

      {!loading && expenses.length === 0 && (
        <p className="text-center text-slate-500 py-10">لا توجد بيانات</p>
      )}

      <EditTransactionModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        transaction={editTarget}
        onSaved={() => fetchExpenses(page)}
      />
    </motion.div>
  );
};


/* ---------------- AccountingPage (root) ---------------- */
const AccountingPage = () => {
  const [activeTab, setActiveTab] = useState("daily");
  const [linkFilters, setLinkFilters] = useState({ date: "", type: "", academicYear: undefined });
const location = useLocation();
    const navigate = useNavigate()
    useEffect(() => {
        const navState = location.state;
        // Check if we were told to auto-open a specific tab
        if (navState?.autoOpenTab) {
            setActiveTab(navState.autoOpenTab); // [cite: 523, 529]
            
            // IMPORTANT: Clear the state
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);
  const openInstallmentsForDate = (date) => {
    setLinkFilters({ date: date || "", type: "", academicYear: "" });
    setActiveTab("installments");
  };
  const openExpensesForDate = (date) => {
    setLinkFilters({ date: date || "", type: "out", academicYear: "" });
    setActiveTab("expenses");
  };
  const openInstallmentsForAcademicYear = (aYear) => {
    setLinkFilters({ date: "", type: "", academicYear: aYear || "" });
    setActiveTab("installments");
  };
  const openExpensesForAcademicYear = (aYear) => {
    setLinkFilters({ date: "", type: "", academicYear: aYear || "" });
    setActiveTab("expenses");
  };

  const tabs = useMemo(() => [
    { id: "daily", label: "صندوق اليوم", icon: CalendarDaysIcon },
    { id: "annual", label: "الميزانية السنوية", icon: BanknotesIcon },
    { id: "payment", label: "إضافة قسط", icon: PlusCircleIcon },
    { id: "expense", label: "صرف نفقة", icon: ReceiptRefundIcon },
    { id: "installments", label: "سجل الأقساط", icon: DocumentTextIcon },
    { id: "expenses", label: "سجل النفقات", icon: DocumentTextIcon },
  ], []);

  return (
    <div dir="rtl" className="bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen p-4 sm:p-6 lg:p-8 font-sans">
      <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 space-y-1">
        <h1 className="text-4xl font-extrabold text-slate-800 tracking-tight">إدارة المحاسبة</h1>
        <p className="text-slate-700 text-lg">نظرة شاملة على العمليات المالية.</p>
      </motion.header>

      <div className="mb-8">
        <nav className="flex space-x-2 space-x-reverse p-1.5 bg-slate-200/70 rounded-xl overflow-x-auto" aria-label="Tabs">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`relative whitespace-nowrap flex-1 px-4 py-3 text-lg font-semibold rounded-lg transition-colors ${activeTab === tab.id ? "text-slate-900" : "text-slate-800 hover:bg-white/60"}`}>
              {activeTab === tab.id && <motion.div layoutId="active-accounting-tab" className="absolute inset-0 bg-white shadow-md rounded-lg" transition={{ type: "spring", stiffness: 300, damping: 30 }} />}
              <span className="relative z-10 flex items-center justify-center gap-2"><tab.icon className="h-6 w-6" />{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      <main>
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }} transition={{ duration: 0.18 }}>
            {activeTab === "daily" && <DailyCashTab onViewInstallments={openInstallmentsForDate} onViewExpenses={openExpensesForDate} />}
            {activeTab === "annual" && <AnnualBudgetTab onOpenInstallmentsForAcademicYear={openInstallmentsForAcademicYear} onOpenExpensesForAcademicYear={openExpensesForAcademicYear} />}
            {activeTab === "payment" && <AddPaymentTab />}
            {activeTab === "expense" && <AddExpenseTab />}
            {activeTab === "installments" && <InstallmentsTab initialDate={linkFilters.date} initialAcademicYear={linkFilters.academicYear} />}
            {activeTab === "expenses" && <ExpensesTab initialDate={linkFilters.date} initialType={linkFilters.type} initialAcademicYear={linkFilters.academicYear} />}
          </motion.div>
        </AnimatePresence>
      </main>

    </div>
  );
};


export default AccountingPage;
